import {useTranslation} from 'react-i18next'
import {useAppDispatch} from '../../../redux/hooks'
import {LecturerStatistics} from '../../../redux/Slice/lecturerSlice'
import {useEffect, useState} from 'react'
import Loader from 'app/custom/loader/Loader'
import ReactApexChart from 'react-apexcharts'
import {ApexOptions} from 'apexcharts'
import CustomSelect from 'app/custom/CustomSelect'
import {PlanningAcademicYears} from '../../../redux/Slice/generalSlice'

const LecStatistics = () => {
  const {t} = useTranslation()
  const dispatch = useAppDispatch()

  const [statistics, setStatistics] = useState<any>({})
  const [loader, setLoader] = useState(true)
  const [academicYears, setAcademicYears] = useState<any>([])
  const [selectedAy, setSelectedAy] = useState<number[]>([])

  const fetchData = (ayIds: any[]) => {
    setLoader(true)
    const fd = new FormData()
    ayIds.forEach((id) => fd.append('academic_year_id[]', id))
    dispatch(LecturerStatistics(fd)).then((res) => {
      if (res.meta.requestStatus === 'fulfilled') {
        setStatistics(res.payload)
        setLoader(false)
      }
    })
  }

  useEffect(() => {
    dispatch(PlanningAcademicYears()).then((res) => {
      if (res.meta.requestStatus === 'fulfilled') {
        const years = res.payload
        setAcademicYears(years)
        const sorted = [...years].sort((a: any, b: any) => a.name.localeCompare(b.name))
        const lastFour = sorted.slice(-2)
        const selectedIds = lastFour.map((y: any) => y.id)
        setSelectedAy(selectedIds)
        fetchData(selectedIds)
      }
    })
  }, [])

  const handleSelectAll = () => {
    const allIds = academicYears.map((ay: any) => ay.id)
    setSelectedAy(allIds)
  }

  const educationData = statistics.education_categories || {}
  const genderData = statistics.gender_categories || {}
  const diversityData = statistics.diversity_categories || {}

  const convertToPercentage = (categoryData: any) => {
    const years = Object.keys(categoryData)
    let result: any = {}

    years.forEach((year) => {
      const items = categoryData[year] ?? {}
      const numericValues = Object.values(items).map((v: any) => Number(v) || 0)
      const total = numericValues.reduce((a, b) => a + b, 0) || 1
      result[year] = {}
      Object.keys(items).forEach((key) => {
        const value = Number(items[key]) || 0
        result[year][key] = parseFloat(((value / total) * 100).toFixed(2))
      })
    })

    return result
  }

  const buildGroupedChart = (categoryData: any, title: string) => {
    const percentData = convertToPercentage(categoryData)
    const years = Object.keys(percentData)

    if (years.length === 0) return null

    const categories = Object.keys(percentData[years[0]])

    const series = categories.map((label) => ({
      name: label,
      data: years.map((y) => percentData[y][label] ?? 0),
    }))

    const fallbackColors = ['#3B82F6', '#10B981', '#F59E0B', '#6366F1', '#14B8A6', '#EF4444']

    const colors = categories.map((label, index) => {
      const safeLabel = (label || '').toString().toLowerCase()
      if (safeLabel === 'female') return '#ff69b4'
      return fallbackColors[index % fallbackColors.length]
    })

    const options: ApexOptions = {
      chart: {type: 'bar'},
      xaxis: {categories: years},
      colors,
      plotOptions: {
        bar: {
          columnWidth: '55%',
          dataLabels: {position: 'top'},
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => {
          return Number(val) > 0 ? `${Math.round(Number(val))}%` : ''
        },
        offsetY: -20,
        style: {fontSize: '12px', colors: ['#000']},
      },
      tooltip: {
        y: {
          formatter: (val) => `${Math.round(Number(val))}%`,
        },
      },
      title: {text: title, align: 'center'},
    }

    return (
      <div className='col-12 mb-5'>
        <div className='card shadow-sm'>
          <div className='card-body'>
            <ReactApexChart options={options} series={series} type='bar' height={350} />
          </div>
        </div>
      </div>
    )
  }

  const buildDiversityGrouped = () => {
    const years = Object.keys(diversityData)
    if (years.length === 0) return null

    let normalized: any = {}

    years.forEach((year) => {
      const row = diversityData[year]

      normalized[year] = {}

      if (!Array.isArray(row)) {
        Object.keys(row).forEach((key) => {
          normalized[year][key] = Number(row[key]) || 0
        })
      } else {
        row.forEach((item) => {
          const key = item.label || 'Unknown'
          normalized[year][key] = Number(item.count) || 0
        })
      }
    })

    const percentData = convertToPercentage(normalized)
    const categories = Object.keys(percentData[years[0]])

    const series = categories.map((label) => ({
      name: label,
      data: years.map((y) => percentData[y]?.[label] ?? 0),
    }))

    const fallbackColors = ['#3B82F6', '#10B981', '#F59E0B', '#6366F1', '#14B8A6', '#EF4444']

    const colors = categories.map((label, index) => fallbackColors[index % fallbackColors.length])

    const options: ApexOptions = {
      chart: {type: 'bar'},
      xaxis: {categories: years},
      colors,
      plotOptions: {
        bar: {
          columnWidth: '55%',
          dataLabels: {position: 'top'},
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => `${Math.round(Number(val))}%`,
        offsetY: -20,
        style: {fontSize: '12px', colors: ['#000']},
      },
      tooltip: {y: {formatter: (val) => `${Math.round(Number(val))}%`}},
      title: {text: t('DASHBOARD.PROFESSOR_DIVERSITY'), align: 'center'},
    }

    return (
      <div className='col-12 mb-5'>
        <div className='card shadow-sm'>
          <div className='card-body'>
            <ReactApexChart options={options} series={series} type='bar' height={350} />
          </div>
        </div>
      </div>
    )
  }
  if (loader) return <Loader />
  return (
    <>
      <div className='card mb-5'>
        <div className='card-header d-none'></div>
        <div className='card-body'>
          <div className='row'>
            <div className='col-md-10'>
              <label className='form-label fw-bold mb-1'>
                {t('LABEL.ACADEMIC_YEAR')}
                <span
                  className='mx-3'
                  style={{cursor: 'pointer', color: '#3699FF'}}
                  onClick={handleSelectAll}
                >
                  {t('GLOBAL.SELECT_ALL')}
                </span>
                <span className='text-danger ms-1'>*</span>
              </label>

              <CustomSelect
                className='input'
                value={academicYears.filter((y) => selectedAy.includes(y.id))}
                onChange={(items: any) => setSelectedAy(items ? items.map((i: any) => i.id) : [])}
                options={academicYears}
                isMulti
              />
            </div>
            <div className='col-md-2 pt-7'>
              <button
                className='btn btn-outline-primary btn-sm fw-bold px-4 w-100'
                onClick={() => selectedAy.length > 0 && fetchData(selectedAy)}
              >
                <span className='fa fa-search mx-2'></span>
                {t('GLOBAL.SEARCH')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {loader && <Loader />}

      {!loader && (
        <div className='row'>
          {buildGroupedChart(educationData, t('DASHBOARD.PROFESSOR_QUALIFICATION'))}

          {buildGroupedChart(genderData, t('DASHBOARD.PROFESSOR_GENDER'))}

          {buildDiversityGrouped()}
        </div>
      )}
    </>
  )
}

export default LecStatistics
